/**
 * Admin Mutual Fund Portfolio Planner.
 *
 * Route: /mf_portfolio_planner/:userUuid?
 * Permission: "MF Portfolio Planner"
 *
 * The user platform runs this planner inside the investor's own mutual-fund
 * hero panel. Here it is a page of its own, scoped to a chosen investor, and it
 * carries three things that surface does not need:
 *
 *  • an investor picker, because an admin arrives without a session identity;
 *  • the investor's live context — profile readiness, holdings, portfolio
 *    report, lumpsum and SIP orders — because the admin is not the person whose
 *    portfolio it is;
 *  • a callback recovery path, for the case where a provider returns to the
 *    top-level tab instead of the popup that opened it.
 *
 * THE USER UUID IN THE ROUTE IS THE ONLY IDENTITY. Anything in the query string
 * — name, email, mobile, SMEST id — is display text carried over from the page
 * that opened this one, and is never sent anywhere.
 *
 * NO CONSENT OTP anywhere in this flow: an admin acts directly on the
 * investor's behalf. Payment-gateway and bank e-mandate authorization are not
 * part of that layer and are preserved exactly as the order APIs require.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { makeGETRequestOnUserService } from "../../../Api/api";
import {
  completeAdminLumpsumPayment,
  completeAdminSip,
  fetchMFAdminDashboard,
  fetchMFSchemeDetails,
  getMFErrorMessage,
  isMFMandatePendingError,
} from "../../../Api/mfAdminInvestments";
import type {
  MFAdminDashboardData,
  MFRecord,
} from "../../../Api/mfAdminInvestments";
import { MF_POPUP_FLAG } from "../../../Helpers/mfPopupReturn";
import {
  isMfUnsupportedAccountType,
  mfUnsupportedAccountReason,
} from "../../../Helpers/mfAccountEligibility";
import { appContext } from "../../../App";
import { hasAdminPermission } from "../../../Helpers/adminPermissions";
import InvestorLeadGate from "../../../components/MutualFunds/InvestorLeadGate";
import {
  fetchPlannerBootstrap,
  generatePlan,
  plannerErrorMessage,
} from "../../../Api/mfPortfolioPlanner";
import type {
  PlanResult,
  PlannerBootstrap,
  PlannerGenerateRequest,
} from "../../../Helpers/portfolioPlanner/types";
import {
  PROSPECT_PLANNER_BOOTSTRAP,
  describePlannerDrift,
} from "../../../Helpers/portfolioPlanner/prospectDefaults";
import type { MFInvestorStatus } from "../../../components/MutualFunds/KycCheckDialog";
import { InlineLoader } from "./PlannerFields";
import PlannerForm from "./PlannerForm";
import PlannerOrders from "./PlannerOrders";
import PlannerReport from "./PlannerReport";
import PlannerUserPicker from "./PlannerUserPicker";
import type { PlannerInvestorOption } from "./PlannerUserPicker";
import {
  PlannerHoldingsTable,
  PlannerInvestorMetrics,
  PlannerOrdersTable,
  PlannerWarnings,
  normalizedStatus,
  summariseHoldings,
} from "./PlannerInvestorPanel";
import styles from "./portfolio-planner.module.css";

interface RouteParams {
  userUuid?: string;
}

type PlannerStage = "form" | "report" | "orders";
type PlannerTab = "planner" | "holdings" | "orders" | "readiness";

const READINESS_ROWS: { key: keyof MFInvestorStatus; label: string }[] = [
  { key: "investor_profile", label: "Investor profile" },
  { key: "identity_document", label: "Identity document" },
  { key: "address", label: "Address" },
  { key: "email_address", label: "Email address" },
  { key: "phone_number", label: "Phone number" },
  { key: "bank_account", label: "Bank account" },
  { key: "nominee", label: "Nominee" },
  { key: "signature", label: "Signature" },
  { key: "kyc_request_creation", label: "KYC request" },
  { key: "esign", label: "eSign" },
  { key: "investment_account", label: "Investment account" },
];

const PortfolioPlanner: React.FC = () => {
  const { userUuid } = useParams<RouteParams>();
  const { authUser } = useContext(appContext);
  // The investor's MF dashboard is its own permission; without it that route
  // refuses entry, so its button is shown disabled with the reason.
  const dashboardBlockedReason = hasAdminPermission(authUser, "MF Investor Profile")
    ? ""
    : "Your admin account does not have the MF Investor Profile permission. A superuser can grant it from Admin Users.";
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  /*
   * Prospect mode: a plan for someone who has no SMEST account.
   *
   * `POST portfolio_planner/generate/` carries no user id — the whole engine
   * runs off the form alone — so the only thing a stranger lacks is the KYC
   * prefill and an investor context, and neither is load-bearing. What they
   * cannot have is orders: every order endpoint needs a uuid, an investment
   * account and completed KYC.
   */
  const prospect = !userUuid && query.get("prospect") === "1";

  /* Display only — the route's uuid is the authoritative identity. */
  const display = useMemo(
    () => ({
      name: query.get("name") || "",
      email: query.get("email") || "",
      mobile: query.get("mobile") || "",
      smestId: query.get("smestId") || "",
    }),
    [query]
  );

  const [bootstrap, setBootstrap] = useState<PlannerBootstrap | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");

  const [context, setContext] = useState<MFAdminDashboardData | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextRefreshing, setContextRefreshing] = useState(false);
  const [investorStatus, setInvestorStatus] = useState<MFInvestorStatus | undefined>();
  const [accountType, setAccountType] = useState("");

  const [plan, setPlan] = useState<PlanResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [stage, setStage] = useState<PlannerStage>("form");
  const [tab, setTab] = useState<PlannerTab>("planner");

  const [callbackMessage, setCallbackMessage] = useState("");
  const [callbackError, setCallbackError] = useState("");
  const [callbackBusy, setCallbackBusy] = useState(false);
  const handledCallbacks = useRef<{ [key: string]: boolean }>({});

  const returnPath = useMemo(
    () => `/mf_portfolio_planner/${encodeURIComponent(userUuid || "")}`,
    [userUuid]
  );

  /* ─────────────────────────── data loading ─────────────────────────── */

  const loadBootstrap = useCallback(async () => {
    if (!userUuid && !prospect) return;
    setBootstrapError("");
    /*
     * A prospect never calls bootstrap. No user id is used for anything on
     * their behalf: the settings come from the checked copy in
     * prospectDefaults, and `generate` needs no id at all.
     */
    if (!userUuid) {
      setBootstrap(PROSPECT_PLANNER_BOOTSTRAP);
      return;
    }
    setBootstrapLoading(true);
    try {
      const live = await fetchPlannerBootstrap(userUuid);
      setBootstrap(live);
      // This investor's bootstrap was fetched anyway; comparing it with the
      // prospect copy costs nothing and turns a stale default into a warning.
      // Guarded on its own so a diagnostic can never fail the investor's load.
      try {
        const drift = describePlannerDrift(live);
        if (drift.length) {
          // eslint-disable-next-line no-console
          console.warn(
            "[Portfolio Planner] The prospect settings copy in " +
              "Helpers/portfolioPlanner/prospectDefaults.ts no longer matches the " +
              "server. Update it:\n" +
              drift.join("\n")
          );
        }
      } catch (driftError) {
        // Nothing to do — the investor's planner is already loaded.
      }
    } catch (error) {
      setBootstrap(null);
      setBootstrapError(plannerErrorMessage(error));
    } finally {
      setBootstrapLoading(false);
    }
  }, [userUuid, prospect]);

  const loadContext = useCallback(
    async (initial: boolean) => {
      if (!userUuid) return;
      if (initial) setContextLoading(true);
      else setContextRefreshing(true);
      try {
        const [dashboard, user] = await Promise.all([
          fetchMFAdminDashboard(userUuid),
          makeGETRequestOnUserService(
            `/user/get/${encodeURIComponent(userUuid)}/`
          ).catch(() => null),
        ]);
        setContext(dashboard);
        setInvestorStatus(
          ((user as any)?.data?.mfInvestorStatus as MFInvestorStatus) || undefined
        );
        setAccountType((user as any)?.data?.accountType || "");
      } catch (error) {
        setContext({
          reports: null,
          purchases: [],
          sipPlans: [],
          redemptions: [],
          switches: [],
          warnings: [getMFErrorMessage(error, "The investor context could not be loaded.")],
        });
      } finally {
        if (initial) setContextLoading(false);
        else setContextRefreshing(false);
      }
    },
    [userUuid]
  );

  useEffect(() => {
    if (!userUuid && !prospect) return;
    loadBootstrap();
    // A prospect has no holdings, orders or readiness to read.
    if (userUuid) loadContext(true);
  }, [userUuid, prospect, loadBootstrap, loadContext]);

  /* ───────────────────── provider callback recovery ─────────────────── */

  /**
   * Orders are placed in a popup, and the /mf-payment-return bridge hands the
   * result back to this window without navigating it. If a provider ever
   * returns to the top-level tab instead — a popup blocked after the fact, a
   * provider that breaks out of its frame — the callback lands here with the
   * plan already gone from memory. Nothing about that is recoverable except the
   * order itself, so the order is finished rather than abandoned half-created.
   *
   * `handledCallbacks` is what stops a re-render, a refresh or a retried
   * callback from running the same completion twice.
   */
  useEffect(() => {
    if (!userUuid) return;
    const paymentId = query.get("mf_admin_payment");
    const mandateId = query.get("mf_admin_sip_mandate");
    const callbackId = paymentId
      ? `payment:${paymentId}`
      : mandateId
      ? `sip:${mandateId}`
      : "";
    if (!callbackId || handledCallbacks.current[callbackId]) return;
    handledCallbacks.current[callbackId] = true;

    const clearParams = () => {
      const clean = new URLSearchParams(location.search);
      Array.from(clean.keys())
        .filter((key) => key.indexOf("mf_admin_") === 0 || key === MF_POPUP_FLAG)
        .forEach((key) => clean.delete(key));
      const suffix = clean.toString();
      history.replace(`${location.pathname}${suffix ? `?${suffix}` : ""}`);
    };

    const run = async () => {
      setCallbackBusy(true);
      setCallbackError("");
      setCallbackMessage(
        paymentId
          ? "Reconciling a payment that returned to this tab…"
          : "Completing a SIP whose bank authorization returned to this tab…"
      );
      try {
        if (paymentId) {
          await completeAdminLumpsumPayment(paymentId);
          setCallbackMessage(
            `Payment reconciled for purchase ${paymentId}. The plan itself is not saved, so it has to be generated again if more orders are needed.`
          );
        } else if (mandateId) {
          const isin = query.get("mf_admin_sip_isin") || "";
          if (!isin) throw new Error("The SIP scheme was not carried back on the callback.");
          const details = await fetchMFSchemeDetails(isin);
          const day = Number(query.get("mf_admin_sip_day"));
          const completed = await completeAdminSip({
            userUuid,
            mandateId,
            scheme: details.data,
            amount: Number(query.get("mf_admin_sip_amount")),
            frequency: query.get("mf_admin_sip_frequency") || "monthly",
            installmentDay: Number.isFinite(day) && day > 0 ? day : undefined,
            installments: Number(query.get("mf_admin_sip_installments")),
            firstInstallmentNow: query.get("mf_admin_sip_first") === "true",
          });
          setCallbackMessage(
            completed.syncWarning
              ? `SIP ${completed.purchasePlanId} is confirmed. Its latest status could not be synchronized yet — check Orders & SIPs shortly.`
              : `SIP ${completed.purchasePlanId} was created after the mandate was approved.`
          );
        }
        await loadContext(false);
        clearParams();
      } catch (error) {
        setCallbackMessage("");
        setCallbackError(
          isMFMandatePendingError(error)
            ? `${getMFErrorMessage(error)} Nothing was created — retry the line once the bank has approved it.`
            : `${getMFErrorMessage(error)} Check this investor's Orders & SIPs before placing anything again.`
        );
        clearParams();
      } finally {
        setCallbackBusy(false);
      }
    };

    run();
  }, [userUuid, query, history, location.pathname, location.search, loadContext]);

  /* ──────────────────────────── derived ─────────────────────────────── */

  const holdings = useMemo(
    () => summariseHoldings(context ? context.reports : null),
    [context]
  );
  const orders = useMemo<MFRecord[]>(() => {
    const lumpsum: MFRecord[] = ((context && context.purchases) || []).map(
      (order) => ({ ...order, order_type: "lumpsum" })
    );
    const sip: MFRecord[] = ((context && context.sipPlans) || []).map((order) => ({
      ...order,
      order_type: "sip",
    }));
    return lumpsum.concat(sip).sort(
      (left, right) =>
        new Date(right.created_at || right.inserted_at || 0).getTime() -
        new Date(left.created_at || left.inserted_at || 0).getTime()
    );
  }, [context]);
  const activeSips = useMemo(
    () =>
      ((context && context.sipPlans) || []).filter((planRow) => {
        const status = normalizedStatus(planRow).toLowerCase();
        return !["cancelled", "failed", "completed", "rejected"].some(
          (value) => status.indexOf(value) >= 0
        );
      }).length,
    [context]
  );
  const accountUnsupported = isMfUnsupportedAccountType(accountType);
  // The plan and its report are still worth producing for an unsupported
  // account; only placement is impossible.
  const kycComplete =
    investorStatus?.are_MF_KYC_Completed === true && !accountUnsupported;
  const investorName =
    (plan && plan.client.name) ||
    display.name ||
    (bootstrap && bootstrap.prefill && bootstrap.prefill.name) ||
    (prospect ? "New prospect" : "MF investor");

  /* ───────────────────────────── actions ────────────────────────────── */

  const onGenerate = async (request: PlannerGenerateRequest) => {
    setGenerating(true);
    setGenerateError("");
    try {
      const result = await generatePlan({ ...request, userId: userUuid });
      setPlan(result);
      setStage("report");
      setTab("planner");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setGenerateError(plannerErrorMessage(error));
    } finally {
      setGenerating(false);
    }
  };

  const openInvestorDashboard = () => {
    if (!userUuid) return;
    const params = new URLSearchParams();
    if (display.name || investorName) params.set("name", display.name || investorName);
    if (display.email) params.set("email", display.email);
    if (display.mobile) params.set("mobile", display.mobile);
    if (display.smestId) params.set("smestId", display.smestId);
    const suffix = params.toString();
    window.open(
      `/mf_investor_profile/${encodeURIComponent(userUuid || "")}/investments${
        suffix ? `?${suffix}` : ""
      }`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const selectInvestor = (investor: PlannerInvestorOption) => {
    const params = new URLSearchParams();
    params.set("name", investor.name);
    if (investor.email) params.set("email", investor.email);
    if (investor.mobile) params.set("mobile", investor.mobile);
    if (investor.smestId) params.set("smestId", investor.smestId);
    history.push(
      `/mf_portfolio_planner/${encodeURIComponent(investor.uuid)}?${params.toString()}`
    );
  };

  /* ───────────────────────────── render ─────────────────────────────── */

  if (!userUuid && !prospect) {
    return (
      <main className={styles.page}>
        <div className={styles.shell}>
          <PlannerUserPicker
            onSelect={selectInvestor}
            onPlanForProspect={() => history.push("/mf_portfolio_planner?prospect=1")}
          />
        </div>
      </main>
    );
  }

  const plannerBody = () => {
    if (bootstrapLoading && !bootstrap) {
      return (
        <div className={styles.skeleton} aria-busy="true" aria-live="polite">
          <div className={styles.skeletonCard} />
          <div className={styles.skeletonCard} />
          <div className={styles.skeletonCard} />
        </div>
      );
    }
    if (!bootstrap) {
      return (
        <div className={styles.gate}>
          <div className={styles.gateInner}>
            <h3>The planner could not be loaded for this investor</h3>
            <p>{bootstrapError || "The planner configuration was not returned."}</p>
            <div className={styles.gateActions}>
              <button
                type="button"
                className={styles.primary}
                disabled={bootstrapLoading}
                aria-busy={bootstrapLoading || undefined}
                onClick={loadBootstrap}
              >
                {bootstrapLoading ? <InlineLoader /> : null}
                {bootstrapLoading ? "Retrying…" : "Try again"}
              </button>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => history.push("/mf_portfolio_planner")}
              >
                Choose another investor
              </button>
            </div>
          </div>
        </div>
      );
    }

    // `userUuid` is named explicitly so the orders branch is typed as having
    // one — prospect mode is precisely the case where there is none.
    if (stage === "orders" && plan && !prospect && userUuid) {
      return (
        <PlannerOrders
          plan={plan}
          userUuid={userUuid}
          returnPath={returnPath}
          placementBlockedReason={
            accountUnsupported
              ? mfUnsupportedAccountReason(accountType)
              : kycComplete
              ? undefined
              : "This investor's MF onboarding is not complete, so the order APIs will reject anything placed for them. Finish their KYC from the MF dashboard first — the plan and its report are unaffected."
          }
          onBack={() => setStage("report")}
          onPlaced={() => loadContext(false)}
        />
      );
    }

    if (stage === "report" && plan) {
      return (
        <PlannerReport
          plan={plan}
          config={bootstrap.config}
          investorName={investorName}
          onBack={() => setStage("form")}
          onPlaceOrders={
            !prospect && plan.orders && plan.orders.length
              ? () => setStage("orders")
              : undefined
          }
        />
      );
    }

    return (
      <PlannerForm
        bootstrap={bootstrap}
        fallbackName={display.name}
        generating={generating}
        generateError={generateError}
        onGenerate={onGenerate}
      />
    );
  };

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.topbar}>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => history.push("/mf_portfolio_planner")}
          >
            <ArrowBackIcon fontSize="small" /> Choose another investor
          </button>
          <div className={styles.topbarActions}>
            <span className={styles.adminBadge}>
              {prospect ? "ADMIN · PROSPECT PLAN" : "ADMIN · ON BEHALF OF INVESTOR"}
            </span>
            <button
              type="button"
              className={styles.ghost}
              disabled={contextRefreshing}
              aria-busy={contextRefreshing || undefined}
              onClick={() => loadContext(false)}
            >
              {contextRefreshing ? <InlineLoader /> : <RefreshIcon fontSize="small" />}
              {contextRefreshing ? "Refreshing…" : "Refresh context"}
            </button>
          </div>
        </div>

        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <p className={styles.eyebrow}>
              {prospect
                ? "Mutual fund portfolio planner · prospect"
                : "Mutual fund portfolio planner"}
            </p>
            <h1>{investorName}</h1>
            <div className={styles.identity}>
              {prospect ? (
                <span>Not a SMEST user — no account, no holdings, no orders</span>
              ) : (
                <>
                  {display.smestId ? <span>SMEST ID · {display.smestId}</span> : null}
                  {display.email ? <span>{display.email}</span> : null}
                  {display.mobile ? <span>+91 {display.mobile}</span> : null}
                  <span>UUID · {userUuid}</span>
                </>
              )}
            </div>
          </div>
          <div className={styles.heroAside}>
            {prospect ? (
              <span className={`${styles.heroState} ${styles.pending}`}>
                <ErrorOutlineIcon fontSize="small" />
                Plan and report only
              </span>
            ) : (
            <>
            <span
              className={`${styles.heroState} ${kycComplete ? styles.ready : styles.pending}`}
            >
              {kycComplete ? (
                <CheckCircleOutlineIcon fontSize="small" />
              ) : (
                <ErrorOutlineIcon fontSize="small" />
              )}
              {kycComplete ? "MF KYC complete" : "MF onboarding incomplete"}
            </span>
            <button
              type="button"
              className={`${styles.primary} ${styles.heroButton}`}
              disabled={Boolean(dashboardBlockedReason)}
              title={dashboardBlockedReason || undefined}
              aria-label={
                dashboardBlockedReason
                  ? `Open MF dashboard — unavailable: ${dashboardBlockedReason}`
                  : undefined
              }
              onClick={openInvestorDashboard}
            >
              <OpenInNewIcon style={{ fontSize: 17 }} aria-hidden="true" />
              Open MF dashboard
            </button>
            </>
            )}
          </div>
        </section>

        {prospect ? (
          <div className={styles.notice}>
            <InfoOutlinedIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
            <span>
              This plan is being built for someone who is not a SMEST user. The
              plan and its downloadable report need no account — but no order
              can be placed until they sign up and finish MF onboarding, so the
              orders step is not offered here. Download the report before
              leaving: plans are not saved.
            </span>
          </div>
        ) : null}

        {!prospect && accountUnsupported ? (
          <div className={styles.noticeBad}>
            <WarningAmberIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
            <span>{mfUnsupportedAccountReason(accountType)}</span>
          </div>
        ) : null}

        {!prospect && !kycComplete && !accountUnsupported ? (
          <div className={styles.noticeWarn}>
            <WarningAmberIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
            <span>
              This investor&apos;s MF onboarding is not complete, so no order can
              be placed for them yet. The plan and its report can still be
              generated and downloaded. Finish the KYC from their MF dashboard.
            </span>
          </div>
        ) : null}

        {callbackMessage ? (
          <div className={styles.notice} role="status">
            {callbackBusy ? <InlineLoader /> : (
              <InfoOutlinedIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
            )}
            <span>{callbackMessage}</span>
          </div>
        ) : null}
        {callbackError ? (
          <div className={styles.noticeBad} role="alert">
            <WarningAmberIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
            <span>{callbackError}</span>
          </div>
        ) : null}

        {prospect ? null : <PlannerWarnings data={context} />}

        {prospect ? null : contextLoading ? (
          <div className={styles.metricGrid}>
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className={styles.skeletonCard} style={{ height: 118 }} />
            ))}
          </div>
        ) : (
          <PlannerInvestorMetrics
            holdings={holdings}
            orders={orders}
            activeSips={activeSips}
          />
        )}

        <section className={styles.contentCard}>
          <nav className={styles.tabs} aria-label="Portfolio planner sections">
            {(prospect
              ? ([["planner", "Planner"]] as [PlannerTab, string][])
              : ([
                  ["planner", "Planner"],
                  ["holdings", "Holdings"],
                  ["orders", "Orders & SIPs"],
                  ["readiness", "Investment readiness"],
                ] as [PlannerTab, string][])
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`${styles.tab} ${tab === key ? styles.activeTab : ""}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className={styles.panel}>
            {tab === "planner" ? plannerBody() : null}

            {tab === "holdings" ? (
              <>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>Current holdings</h2>
                    <p>
                      From this investor&apos;s portfolio report — the same
                      source the MF dashboard reads.
                    </p>
                  </div>
                </div>
                {contextLoading ? (
                  <div className={styles.skeletonCard} style={{ height: 190 }} />
                ) : (
                  <PlannerHoldingsTable holdings={holdings} />
                )}
              </>
            ) : null}

            {tab === "orders" ? (
              <>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>Orders and SIP instructions</h2>
                    <p>
                      Everything already placed for this investor. Anything the
                      planner places lands here too.
                    </p>
                  </div>
                </div>
                {contextLoading ? (
                  <div className={styles.skeletonCard} style={{ height: 190 }} />
                ) : (
                  <PlannerOrdersTable orders={orders} />
                )}
              </>
            ) : null}

            {tab === "readiness" ? (
              <>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>Investment readiness</h2>
                    <p>
                      Every step the MF onboarding checks. All of them have to
                      pass before an order can be placed.
                    </p>
                  </div>
                </div>
                {contextLoading ? (
                  <div className={styles.skeletonCard} style={{ height: 190 }} />
                ) : investorStatus ? (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {READINESS_ROWS.map((row) => {
                          const done = investorStatus[row.key] === true;
                          return (
                            <tr key={row.key as string}>
                              <td className={styles.strong}>{row.label}</td>
                              <td>
                                <span className={done ? styles.tagGood : styles.tagWarn}>
                                  {done ? "Complete" : "Pending"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles.empty}>
                    This investor&apos;s MF readiness could not be read. Open
                    their MF dashboard to check it.
                  </div>
                )}
              </>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
};

/**
 * With a uuid the planner is about one investor, so an admin limited to their
 * own leads is checked first. The picker and prospect mode carry no uuid and
 * pass straight through.
 */
const PortfolioPlannerPage: React.FC = () => {
  const { userUuid } = useParams<RouteParams>();
  return (
    <InvestorLeadGate
      userUuid={userUuid}
      backTo={{ label: "Choose one of your investors", path: "/mf_portfolio_planner" }}
    >
      <PortfolioPlanner />
    </InvestorLeadGate>
  );
};

export default PortfolioPlannerPage;
